import { useCallback, useMemo } from "react";
import { useDashboardEditorContext } from "../../../../../../hook/context/editor";
import { type BlockPlotModelT } from "../../../../../../schema/block/plot";
import { useDashboardEditorState } from "../../../../../../hook/store/editor";

export function useBlockOptionUpdateCallback() {
  const editor = useDashboardEditorContext();
  const editorState = useDashboardEditorState(editor?.store);
  const block = useMemo(
    () =>
      editorState?.blocks.find(
        b => editor?.block?.kind === "PLOT" && b.id === editor?.block?.id,
      ) as BlockPlotModelT | undefined,
    [editor?.block?.id, editor?.block?.kind, editorState?.blocks],
  );

  return useCallback(
    (
      key: keyof NonNullable<BlockPlotModelT["config"]["options"]>,
      value: NonNullable<BlockPlotModelT["config"]["options"]>[typeof key],
    ) => {
      if (!block) return;

      void editor?.updateBlock({
        ...block,
        config: {
          ...block.config,
          options: {
            ...block.config.options,
            [key]: value,
          },
        },
      });
    },
    [block, editor],
  );
}

export function useSyncedBlockOptionUpdateCallback() {
  const editor = useDashboardEditorContext();
  const editorState = useDashboardEditorState(editor?.store);
  const plotBlocks = useMemo(
    () =>
      editorState?.blocks.filter((block): block is BlockPlotModelT => block.kind === "PLOT") || [],
    [editorState?.blocks],
  );

  return useCallback(
    (
      key: keyof NonNullable<BlockPlotModelT["config"]["options"]>,
      value: NonNullable<BlockPlotModelT["config"]["options"]>[typeof key],
    ) => {
      if (!editor || !plotBlocks.length) return;

      void editor?.updateManyBlocks(
        plotBlocks.map(block => ({
          ...block,
          config: {
            ...block.config,
            options: {
              ...block.config.options,
              [key]: value,
            },
          },
        })),
      );
    },
    [editor, plotBlocks],
  );
}
